import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import {
  ANSWER_OPTIONS,
  type AnswerOption,
  type Answers,
} from '@/modules/simulado/domain/entities/answer.entity';

interface AnswerSheetProps {
  totalQuestions: number;
  optionCount: 4 | 5;
  answers: Answers;
  /** Marca, ou desmarca com `null` quando a alternativa já era a marcada. */
  onAnswer: (question: number, answer: AnswerOption | null) => void;
  /** O botão de enviar: recebe o foco quando não sobra questão em branco. */
  submitRef?: RefObject<HTMLButtonElement | null>;
}

/**
 * A próxima questão em branco a partir de `from`, dando a volta no fim da
 * folha. `null` quando todas estão marcadas.
 */
function findNextBlank(
  answers: Answers,
  from: number,
  total: number,
): number | null {
  for (let offset = 0; offset < total; offset++) {
    const question = ((from - 1 + offset + total) % total) + 1;
    if (!answers[question]) return question;
  }
  return null;
}

/**
 * A folha do gabarito: uma linha por questão, com as alternativas da prova
 * (A–D ou A–E). É a `AnswerSheetStep` do `enamed-feedback-lp`, **com o
 * preenchimento pelo teclado** de lá:
 *
 * - a letra (A–E) marca a questão em foco, e a mesma letra de novo desmarca;
 * - ↑ e ↓ andam entre as questões;
 * - marcar leva o foco à **próxima em branco** — quem copia a folha da prova
 *   digita as letras em sequência sem tocar no mouse —, e quando não sobra
 *   nenhuma o foco vai ao botão de enviar.
 *
 * **Tabindex móvel**: só a linha ativa entra na ordem do Tab, então um Tab
 * atravessa a folha inteira e chega ao envio. As alternativas saem da ordem do
 * Tab; continuam clicáveis e com nome para o leitor de tela.
 *
 * Marca na hora, sem o "confirmar" do ensaio: aqui a pessoa copia o que já
 * respondeu, não decide. A numeração desce em colunas (CSS), e o DOM segue a
 * ordem de leitura, que é a mesma das setas.
 */
export function AnswerSheet({
  totalQuestions,
  optionCount,
  answers,
  onAnswer,
  submitRef,
}: AnswerSheetProps) {
  const options = ANSWER_OPTIONS.slice(0, optionCount);
  const numbers = Array.from({ length: totalQuestions }, (_, i) => i + 1);
  const rowRefs = useRef(new Map<number, HTMLLIElement>());
  const [active, setActive] = useState(
    () => findNextBlank(answers, 1, totalQuestions) ?? 1,
  );

  const registerRow = useCallback(
    (question: number, el: HTMLLIElement | null) => {
      if (el) rowRefs.current.set(question, el);
      else rowRefs.current.delete(question);
    },
    [],
  );

  const focusRow = useCallback((question: number) => {
    setActive(question);
    rowRefs.current.get(question)?.focus();
  }, []);

  // Abre na primeira em branco: quem volta ao rascunho continua de onde parou.
  // Só na montagem — depois, quem move o foco é a pessoa.
  useEffect(() => {
    focusRow(findNextBlank(answers, 1, totalQuestions) ?? 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (question: number, option: AnswerOption) => {
    const isUnmarking = answers[question] === option;
    onAnswer(question, isUnmarking ? null : option);
    // Quem desmarcou quer corrigir esta questão, não ser levado adiante.
    if (isUnmarking) return;

    // `answers` ainda é o do render anterior: projeta a marcação atual.
    const next = findNextBlank(
      { ...answers, [question]: option },
      question + 1,
      totalQuestions,
    );
    if (next === null) submitRef?.current?.focus();
    else focusRow(next);
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLLIElement>,
    question: number,
  ) => {
    // Atalhos do navegador (Ctrl+C, Cmd+A…) passam direto.
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const option = options.find(
      (candidate) => candidate === event.key.toUpperCase(),
    );
    if (option) {
      event.preventDefault();
      select(question, option);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusRow(Math.min(question + 1, totalQuestions));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusRow(Math.max(question - 1, 1));
    }
  };

  return (
    <ol className="sheet">
      {numbers.map((number) => {
        const selected = answers[number];
        const isActive = active === number;

        return (
          <li
            key={number}
            ref={(el) => registerRow(number, el)}
            role="group"
            aria-label={`Questão ${number}`}
            tabIndex={isActive ? 0 : -1}
            onFocus={() => setActive(number)}
            onKeyDown={(event) => handleKeyDown(event, number)}
            className={[
              'sheet-row',
              selected ? 'is-marked' : '',
              isActive ? 'is-focused' : '',
            ].join(' ')}
          >
            <span className="sheet-num">{number}</span>
            <span className="sheet-options">
              {options.map((option) => {
                const isSelected = selected === option;
                return (
                  <button
                    key={option}
                    type="button"
                    tabIndex={-1}
                    className={`sopt ${isSelected ? 'is-marked' : ''}`}
                    aria-pressed={isSelected}
                    aria-label={`Questão ${number}, alternativa ${option}`}
                    onClick={() => select(number, option)}
                  >
                    {option}
                  </button>
                );
              })}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
